/**
 * Connect Page
 * Social discovery hub: experts, ritualists, communities, rituals
 * Forces dark theme on mount, restores previous theme on unmount
 * Each section fetches data independently via its own hook
 */

import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTheme } from "@/context/ThemeContext"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useCommunityJoinActions } from "@/features/communities/hooks/useCommunityMembership"
import { useFollowActions } from "@/features/profile/hooks/useFollowActions"
import { BookAnExpert } from "../components/BookAnExpert"
import { ConnectHero } from "../components/ConnectHero"
import { ExploreCommunities } from "../components/ExploreCommunities"
import { FeaturedCommunityRituals } from "../components/FeaturedCommunityRituals"
import { FeaturedRitualist } from "../components/FeaturedRitualist"
import { HighlightedRitualists } from "../components/HighlightedRitualists"
import { useCommunitySearch } from "../hooks/useCommunitySearch"
import { useExperts } from "../hooks/useExperts"
import {
  useCarouselCommunities,
  useFeaturedCommunityRituals,
  useFeaturedRitualist,
  useHighlightedRitualists,
} from "../hooks/useFeaturedContent"
import type { ConnectFilters } from "../types/connect.types"

const DEFAULT_FILTERS: ConnectFilters = {
  search: "",
  sortBy: "nearest",
  show: "local",
  intentions: [],
  spiritualTraditions: [],
}

export function ConnectScreen() {
  const { theme, setTheme } = useTheme()
  const previousThemeRef = useRef(theme)
  const queryClient = useQueryClient()
  const user = useAuthStore(state => state.user)

  // Force dark theme on mount, restore on unmount
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only effect to set/restore theme
  useEffect(() => {
    previousThemeRef.current = theme
    if (theme !== "dark") {
      setTheme("dark")
    }
    return () => {
      if (previousThemeRef.current !== "dark") {
        setTheme(previousThemeRef.current)
      }
    }
  }, [])

  // ─── Data Fetching ──────────────────────────────────────────────────

  const { communities: carouselCommunities, isLoading: carouselLoading } = useCarouselCommunities()
  const { experts, isLoading: expertsLoading } = useExperts({ limit: 6 })
  const { ritualist: featuredRitualist, isLoading: featuredLoading } = useFeaturedRitualist()
  const { ritualists: highlightedRitualists, isLoading: highlightedLoading } =
    useHighlightedRitualists()
  const { rituals: communityRituals, isLoading: communityRitualsLoading } =
    useFeaturedCommunityRituals()

  // ─── Explore Communities (with filters + infinite scroll) ──────────

  const [filters, setFilters] = useState<ConnectFilters>(DEFAULT_FILTERS)

  const {
    communities: exploreCommunities,
    isLoading: exploreLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCommunitySearch(filters)

  const handleFiltersChange = useCallback((newFilters: ConnectFilters) => {
    setFilters(newFilters)
  }, [])

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, fetchNextPage])

  // ─── Follow/Join/Message Actions ────────────────────────────────────

  const { follow, unfollow } = useFollowActions()
  const { join: joinCommunity } = useCommunityJoinActions()

  const handleFollowRitualist = useCallback(
    (ritualistId: string) => {
      follow(ritualistId, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["connect-highlighted-ritualists"] })
          queryClient.invalidateQueries({ queryKey: ["connect-featured-ritualist"] })
          queryClient.invalidateQueries({ queryKey: ["connect-experts"] })
        },
      })
    },
    [follow, queryClient],
  )

  const handleUnfollowRitualist = useCallback(
    (ritualistId: string) => {
      unfollow(ritualistId, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["connect-highlighted-ritualists"] })
          queryClient.invalidateQueries({ queryKey: ["connect-featured-ritualist"] })
          queryClient.invalidateQueries({ queryKey: ["connect-experts"] })
        },
      })
    },
    [unfollow, queryClient],
  )

  const handleJoinCommunity = useCallback(
    (communityId: string) => {
      joinCommunity(communityId, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["connect-featured-community-rituals"] })
          queryClient.invalidateQueries({ queryKey: ["connect-communities"] })
        },
      })
    },
    [joinCommunity, queryClient],
  )

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <div className="min-h-screen">
      {/* Hero + Carousel */}
      {carouselLoading ? (
        <SectionSkeleton className="h-[400px]" section="hero" />
      ) : (
        <ConnectHero communities={carouselCommunities} />
      )}

      {/* Book an Expert */}
      {expertsLoading ? (
        <SectionSkeleton className="h-[300px]" />
      ) : (
        <BookAnExpert
          experts={experts}
          onFollow={handleFollowRitualist}
          onUnfollow={handleUnfollowRitualist}
        />
      )}

      {/* Featured Ritualist */}
      {featuredLoading ? (
        <SectionSkeleton className="h-[350px]" />
      ) : (
        <FeaturedRitualist
          ritualist={featuredRitualist}
          onFollow={handleFollowRitualist}
          onUnfollow={handleUnfollowRitualist}
        />
      )}

      {/* Highlighted Ritualists */}
      {highlightedLoading ? (
        <SectionSkeleton className="h-[200px]" />
      ) : (
        <HighlightedRitualists
          ritualists={highlightedRitualists}
          onFollow={handleFollowRitualist}
          onUnfollow={handleUnfollowRitualist}
        />
      )}

      {/* Featured Community Rituals */}
      {communityRitualsLoading ? (
        <SectionSkeleton className="h-[350px]" />
      ) : (
        <FeaturedCommunityRituals
          rituals={communityRituals}
          onJoinCommunity={handleJoinCommunity}
        />
      )}

      {/* Explore Communities */}
      <ExploreCommunities
        communities={exploreCommunities}
        isLoading={exploreLoading || isFetchingNextPage}
        hasMore={hasNextPage}
        onLoadMore={handleLoadMore}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        userCity={user?.city}
        userState={user?.state}
      />
    </div>
  )
}

function SectionSkeleton({ className = "", section }: { className?: string; section?: string }) {
  return (
    <div className={`py-8 md:py-12 ${className}`} aria-hidden="true">
      <div className="max-w-360 mx-auto px-5 md:px-17.5">
        <div className="h-6 w-48 mb-4 bg-card rounded animate-pulse" />
        <div className="h-4 w-72 mb-6 bg-card rounded animate-pulse" />
      </div>
      <div
        className={`flex gap-4 ${section === "hero" ? "" : "max-w-360 mx-auto px-5 md:px-17.5"}`}
      >
        <div className="flex-1 h-[200px] rounded-xl bg-card animate-pulse" />
        <div className="flex-1 h-[200px] rounded-xl bg-card animate-pulse hidden md:block" />
        <div className="flex-1 h-[200px] rounded-xl bg-card animate-pulse hidden lg:block" />
      </div>
    </div>
  )
}
